import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileWord } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Box,
  Button,
  DataTable,
  Grid,
  GridItem,
  HStack,
  Text,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { EmptyDataCard, hiddenTableHeaderLabel } from 'components';
import { useFileDownload } from 'hooks';
import { Consultation, PastResponse } from 'types';
import { useResponse } from '../../hooks';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

export type PastResponseProps = {
  consultation: Consultation;
  hideTypeDefinition: boolean;
};

export const PastResponses: React.FC<PastResponseProps> = ({
  consultation,
  hideTypeDefinition = false,
}) => {
  const { t } = useTranslation('consultation_response');

  const { tvs } = useThemedValueSwitch();
  const isMobile = useIsMobile();
  const { pastResponses } = useResponse(consultation.id);
  const { openResponseDocument } = useFileDownload();
  const formatLocaleDate = useFormatLocaleDate();

  const fileName = (response: PastResponse) => (
    <HStack spacing={2} alignItems='flex-start'>
      <FaFileWord
        color={COLOURS.blue[400]}
        style={{ flexShrink: 0, width: '16px', height: '16px' }}
      />
      <Text fontSize='sm' fontWeight='normal' display='flex'>
        <span style={{ marginLeft: '4px', flexShrink: 1 }}>{response.document?.name}</span>
      </Text>
    </HStack>
  );

  const handleOpenDocument = (pastResponse: PastResponse) =>
    pastResponse.document?.id &&
    openResponseDocument({
      consultationId: consultation.id,
      responseId: pastResponse.id,
      documentId: pastResponse.document.id,
    });

  const renderMobile = pastResponses?.length ? (
    <>
      {pastResponses.map((pastResponse) => (
        <Grid
          gridGap={6}
          backgroundColor={COLOURS.gray[100]}
          border={`1px solid ${COLOURS.gray[300]}`}
          borderRadius={5}
          my={3}
          p={4}
          key={pastResponse.id}
        >
          <GridItem>
            <Box>
              <Button
                size='xs'
                variant='secondary'
                float='right'
                onClick={() => handleOpenDocument(pastResponse)}
              >
                {t('consultation_view', 'View')}
              </Button>
              <strong>{t('response_type', 'Response type')}</strong>
            </Box>
            {pastResponse.type}
          </GridItem>
          <GridItem>
            <Box>
              <strong>{t('response_document', 'Response document')}</strong>
              <br />
            </Box>
            {fileName(pastResponse)}
          </GridItem>
          <GridItem>
            <Box>
              <strong>{t('response_date', 'Response date')}</strong>
              <br />
            </Box>
            {formatLocaleDate(pastResponse.responseDate)}
          </GridItem>
        </Grid>
      ))}
    </>
  ) : (
    <Box p={8}>
      <EmptyDataCard message={t('no_responses_message', 'No responses available')} />
    </Box>
  );

  const renderDesktop = (
    <>
      {pastResponses?.length ? (
        <DataTable
          size='sm'
          sx={{
            my: tvs({ tq: 8 }),
            mb: tvs({ tq: 6 }),
          }}
          disablePagination
          disableSortRemove
          initialState={{
            sortBy: [
              {
                id: 'responseDate',
                desc: true,
              },
            ],
          }}
          data={pastResponses}
          columns={[
            {
              accessor: 'type',
              Header: () => t('response_type', 'Response type'),
              disableSortBy: true,
              width: 160,
            },
            ...(hideTypeDefinition
              ? []
              : [
                  {
                    accessor: 'typeDefinition',
                    Header: () => t('response_type_definition', 'Response type definition'),
                    disableSortBy: true,
                    width: 'auto',
                  },
                ]),
            {
              accessor: 'documentName',
              Header: () => t('response_document', 'Response document'),
              disableSortBy: true,
              width: 210,
              Cell: (cellProps: { row: { original: PastResponse } }) =>
                fileName(cellProps.row.original),
            },
            {
              accessor: 'responseDate',
              Header: () => t('response_date', 'Response date'),
              Cell: (cellProps: { value: string }) => formatLocaleDate(cellProps.value),
              width: 190,
            },
            {
              accessor: 'documentId',
              disableSortBy: true,
              Header: () => hiddenTableHeaderLabel(t('document_label', 'document id')),
              width: 100,
              Cell: (cellProps: { row: { original: PastResponse } }) => (
                <Button
                  float='right'
                  size='xs'
                  variant='secondary'
                  onClick={() => handleOpenDocument(cellProps.row.original)}
                >
                  {t('consultation_view', 'View')}
                </Button>
              ),
            },
          ]}
        />
      ) : (
        <EmptyDataCard message={t('no_responses_message', 'No responses available')} />
      )}
    </>
  );

  return isMobile ? renderMobile : renderDesktop;
};
